import { ElectronicTradingWidgetProps } from '@cian/frontend-electronic-trading-widget';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { useGetIsElectronicTrading } from '../../../../../../../shared/hooks/useGetIsElectronicTrading';
import { useApplicationContext } from '../../../../../../../shared/utils/applicationContext';
import { mapCalltrackingPhoneToIPhone, requestOfferDynamicPhone } from '../../../../api/offerDynamicCalltracking';
import { JoinElectronicTradingButton } from '../../../../components/JoinElectronicTradingButton/JoinElectronicTradingButton';
import {
  trackJoinTradeClick,
  trackShowTradingWidgetModalClick,
} from '../../../../components/JoinElectronicTradingButton/tracking';
import { useOfferContext } from '../../../../context';
import { useDynamicPhoneErrorToast } from '../../../../utils/useDynamicPhoneErrorToast';
import { useOfferDynamicCalltrackingEnabled } from '../../../../utils/useOfferDynamicCalltrackingEnabled';
import { formatPhone } from '../AgentPhoneButtonContainer';

export const JoinElectronicTradingButtonContainer: React.FC = () => {
  const { config, httpApi, logger } = useApplicationContext();
  const { offer, agentImageLink, agentName, tracking } = useOfferContext();

  const isElectronicTrading = useGetIsElectronicTrading();
  const isDynamicCalltrackingEnabled = useOfferDynamicCalltrackingEnabled();
  const showPhoneErrorToast = useDynamicPhoneErrorToast();

  const [isModalVisible, setModalVisible] = React.useState(false);
  const [isPhoneLoading, setPhoneLoading] = React.useState(false);
  const [dynamicPhones, setDynamicPhones] = React.useState<string[] | null>(null);

  const legalLink = config.get<string>('electronicTrading.legalDocsUrl') ?? '';

  const trackJoinTrade = React.useCallback((): void => {
    trackJoinTradeClick(offer);
  }, [offer]);

  const closeModal = React.useCallback((): void => setModalVisible(false), []);

  const handleButtonClick = React.useCallback(async (): Promise<void> => {
    if (!isDynamicCalltrackingEnabled) {
      trackShowTradingWidgetModalClick(offer);
      setModalVisible(true);

      return;
    }

    // clickId рождается ДО события и запроса — им аналитика чистит бэковые события КТ
    const clickId = uuid();
    trackShowTradingWidgetModalClick(offer, clickId);
    setPhoneLoading(true);

    const result = await requestOfferDynamicPhone({ offerId: offer.id, clickId }, { config, httpApi, logger });

    const hasStaticPhones = Boolean(offer.phones?.some(phone => phone?.number));

    if (result?.status === 'ok') {
      setDynamicPhones(result.phones.map(phone => formatPhone(mapCalltrackingPhoneToIPhone(phone))));
    } else if (!hasStaticPhones) {
      // модалку открываем и без номера: в ней есть переход к торгам
      setDynamicPhones(null);
      logger.warning('OfferDynamicCalltracking: номер недоступен, в модалке торгов показан тост-ошибка', {
        offerId: offer.id,
        clickId,
        status: result?.status ?? null,
      });
      showPhoneErrorToast();
    } else if (result?.status === 'failedToAllocatePhone') {
      // TODO SEL-998: по контракту реальный номер не раскрывается — ждём чат-фоллбэк
      setDynamicPhones(null);
      logger.warning('OfferDynamicCalltracking: failedToAllocatePhone, в модалке торгов показана статика', {
        offerId: offer.id,
        clickId,
      });
    } else {
      setDynamicPhones(null);
      tracking.trackShowStatNumber(clickId);
    }

    setPhoneLoading(false);
    setModalVisible(true);
  }, [isDynamicCalltrackingEnabled, offer, config, httpApi, logger, tracking, showPhoneErrorToast]);

  /**
   * Эти события трекать не требуется. Добавить, если требования поменяются.
   */
  const trackLegalLinkClick = React.useCallback((): void => {}, []);
  const trackPublisherPhoneNumberClick = React.useCallback((): void => {}, []);

  const widgetProps = React.useMemo((): ElectronicTradingWidgetProps => {
    const staticPhones = offer.phones?.map(formatPhone) || [];
    const prettyPhones = dynamicPhones ?? staticPhones;

    return {
      deviceType: 'desktop',
      joinTradeButtonLink: offer.tradingLink || '',
      legalLink,
      publisherAvatarSrc: agentImageLink || offer.user?.agentAvatarUrl || null,
      publisherName: agentName || offer.user?.agencyName || '',
      publisherPhoneNumbers: prettyPhones,
      publisherTypeLabel: null,
      trackJoinTrade,
      trackLegalLinkClick,
      trackPublisherPhoneNumberClick,
    };
  }, [
    legalLink,
    agentImageLink,
    agentName,
    dynamicPhones,
    offer.tradingLink,
    offer.phones,
    offer.user,
    trackJoinTrade,
    trackLegalLinkClick,
    trackPublisherPhoneNumberClick,
  ]);

  if (!isElectronicTrading) {
    return null;
  }

  return (
    <JoinElectronicTradingButton
      {...widgetProps}
      loading={isPhoneLoading}
      open={isModalVisible}
      onButtonClick={handleButtonClick}
      onClose={closeModal}
    />
  );
};
